import React, { memo, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  RLayer,
  RMap,
  RMapContextProvider,
  RSource,
} from "maplibre-react-components";
import type {
  DataDrivenPropertyValueSpecification,
  FillLayerSpecification,
  Map as MapLibreMap,
} from "maplibre-gl";
import { DEFAULT_SOURCE_TABLE } from "@/store/AdminToolsProvider";
import { tariffModeToLayer } from "@/types/base";
import { makeStepExpression } from "@/utils/helpers";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";

const CENTER: [number, number] = [37.6151, 55.7569];
const MAP_STYLE = "/assets/mapStyle.json";
const AUTH_TARIFF_MODE = "t_econom";
const AUTH_PRICE_FIELD = tariffModeToLayer[AUTH_TARIFF_MODE];
const AUTH_TARIFF_CONFIG = getTariffModeConfig(AUTH_TARIFF_MODE);
const TEMPLATE_STOPS = [50, 100, 200, 500, 1000, 1500];
const fillColor = makeStepExpression(
  AUTH_PRICE_FIELD,
  100,
  1500,
  AUTH_TARIFF_CONFIG.colorScale,
  TEMPLATE_STOPS,
  "relative"
) as DataDrivenPropertyValueSpecification<string>;

const coefficientFill: FillLayerSpecification = {
  id: "auth-coefficient-fill",
  type: "fill",
  source: "auth-hex-src",
  "source-layer": DEFAULT_SOURCE_TABLE,
  minzoom: 5,
  filter: [">", ["coalesce", ["get", AUTH_PRICE_FIELD], 0], 0],
  paint: {
    "fill-color": fillColor,
    "fill-opacity": [
      "interpolate",
      ["linear"],
      ["zoom"],
      8,
      0.82,
      12,
      0.66,
      14,
      0.42,
    ],
  },
};

const AuthCoefficientLayer = () => (
  <>
    <RSource
      id="auth-hex-src"
      type="vector"
      tiles={[`${__MARTIN_URL__}/${DEFAULT_SOURCE_TABLE}/{z}/{x}/{y}.pbf`]}
      minzoom={0}
      maxzoom={14}
    />
    <RLayer {...coefficientFill} />
  </>
);

function AuthMapBackground() {
  const [mapLoaded, setMapLoaded] = useState(false);

  return (
    <RMapContextProvider>
      <RMap
        id="auth-background-map"
        initialCenter={CENTER}
        initialZoom={11.2}
        mapStyle={MAP_STYLE}
        style={{ width: "100%", height: "100%" }}
        onMounted={(map: MapLibreMap) => {
          map.dragPan.disable();
          map.scrollZoom.disable();
          map.boxZoom.disable();
          map.keyboard.disable();
          map.doubleClickZoom.disable();
          map.dragRotate.disable();
          map.touchZoomRotate.disable();
          map.showTileBoundaries = false;
          map.once("load", () => setMapLoaded(true));
        }}
      >
        {mapLoaded && <AuthCoefficientLayer />}
      </RMap>
    </RMapContextProvider>
  );
}

export default memo(AuthMapBackground);
